import React, { useState, useRef, useEffect } from "react";
import "./musicplayer.css";

import { Wifi, Play, Player, Cloud, Music, Pause } from "../../assets/SvgIcons";

// Хелпери для підтримки двох форматів:
//   API формат:    { audio_file, cover_image, artist, title, spotify_link, apple_music_link, youtube_link, soundcloud_link }
//   Legacy формат: { src, artwork, artist, trackTitle, spotify, appleMusic, youTubeMusic, soundCloud }
const getSrc        = (t) => t.audio_file || t.src || "";
const getArtwork    = (t) => t.cover_image || t.artwork || null;
const getArtist     = (t) => t.artist || "";
const getTitle      = (t) => t.title || t.trackTitle || "";
const getSpotify    = (t) => t.spotify_link || t.spotify || null;
const getApple      = (t) => t.apple_music_link || t.appleMusic || null;
const getYouTube    = (t) => t.youtube_link || t.youTubeMusic || null;
const getSoundCloud = (t) => t.soundcloud_link || t.soundCloud || null;

const MusicListPlayer = ({ tracks = [] }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onMeta  = () => setDuration(audio.duration);
    const onTime  = () => setCurrentTime(audio.currentTime);
    const onEnded = () => {
      if (currentTrackIndex < tracks.length - 1) {
        handleTrackChange(currentTrackIndex + 1);
      } else {
        setIsPlaying(false);
        audio.pause();
        setCurrentTime(0);
      }
    };

    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnded);
    };
  }, [currentTrackIndex, tracks]);

  const togglePlayPause = (index) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (currentTrackIndex === index) {
      isPlaying ? audio.pause() : audio.play();
      setIsPlaying(!isPlaying);
    } else {
      handleTrackChange(index);
    }
  };

  const formatTime = (time) => {
    const m = Math.floor(time / 60);
    const s = Math.floor(time % 60);
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const handleProgressChange = (e) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = e.target.value;
      setCurrentTime(e.target.value);
    }
  };

  const handleTrackChange = (index) => {
    setCurrentTrackIndex(index);
    setCurrentTime(0);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      setTimeout(() => audioRef.current.play(), 0);
      setIsPlaying(true);
    }
  };

  if (tracks.length === 0) {
    return <div className="music-player">No tracks available</div>;
  }

  const current = tracks[currentTrackIndex] || {};

  return (
    <>
      <audio ref={audioRef} src={getSrc(current)} preload="metadata" />

      <div className="music-player-section-card">
        <div className="d-flex justify-content-start align-items-center flex-column player-cards-wrapper pb-0">
          {tracks.map((track, index) => (
            <div
              className={`player-card ${currentTrackIndex === index ? "active" : ""}`}
              key={track.id || index}
            >
              <div className="w-100 d-flex justify-content-between align-items-center gap-3 gap-lg-4">
                <div className="music-image">
                  {getArtwork(track) && (
                    <img src={getArtwork(track)} alt={getTitle(track)} />
                  )}
                </div>
                <div className="w-100 music-content">
                  <h3>{getArtist(track)}</h3>
                  <p>{getTitle(track)}</p>
                  {/* Посилання на стрімінги — десктоп */}
                  <div className="d-flex justify-content-start align-items-center gap-3 d-none d-lg-flex">
                    {getSpotify(track) && (
                      <div className="music-tools">
                        <a href={getSpotify(track)} target="_blank" rel="noreferrer"><Wifi /></a>
                      </div>
                    )}
                    {getApple(track) && (
                      <div className="music-tools">
                        <a href={getApple(track)} target="_blank" rel="noreferrer"><Player /></a>
                      </div>
                    )}
                    {getYouTube(track) && (
                      <div className="music-tools">
                        <a href={getYouTube(track)} target="_blank" rel="noreferrer"><Music /></a>
                      </div>
                    )}
                    {getSoundCloud(track) && (
                      <div className="music-tools">
                        <a href={getSoundCloud(track)} target="_blank" rel="noreferrer"><Cloud /></a>
                      </div>
                    )}
                  </div>
                </div>
                <div className="play-and-pause controls" onClick={() => togglePlayPause(index)}>
                  {isPlaying && currentTrackIndex === index ? <Play /> : <Pause />}
                </div>
              </div>

              {/* Посилання на стрімінги — мобайл */}
              <div className="w-100 d-flex justify-content-between align-items-center gap-4 d-lg-none">
                <div className="w-100 music-content">
                  <div className="d-flex justify-content-start align-items-center gap-3">
                    {getSpotify(track) && (
                      <div className="music-tools">
                        <a href={getSpotify(track)} target="_blank" rel="noreferrer"><Wifi /></a>
                      </div>
                    )}
                    {getApple(track) && (
                      <div className="music-tools">
                        <a href={getApple(track)} target="_blank" rel="noreferrer"><Player /></a>
                      </div>
                    )}
                    {getYouTube(track) && (
                      <div className="music-tools">
                        <a href={getYouTube(track)} target="_blank" rel="noreferrer"><Music /></a>
                      </div>
                    )}
                    {getSoundCloud(track) && (
                      <div className="music-tools">
                        <a href={getSoundCloud(track)} target="_blank" rel="noreferrer"><Cloud /></a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Прогрес-бар — тільки для активного треку */}
              {currentTrackIndex === index && (
                <div className="w-100 booking-border d-lg-none" />
              )}
              {currentTrackIndex === index && (
                <div className="w-100">
                  <div className="time-info">
                    <div className="w-100 d-flex rounded position-relative mt-1 mb-1">
                      <input
                        type="range"
                        min="0"
                        max={duration || 1}
                        value={currentTime}
                        onChange={handleProgressChange}
                      />
                      <div
                        className="position-absolute bottom-0 start-0 time-line-bar"
                        style={{ width: `${(currentTime / duration) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-between info-time-line">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default MusicListPlayer;
